import { Selection, useCreateRevision } from 'hooks/queries/layers/revisions/useCreateRevision'
import { useLayoutEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { AlmanacEditorState, useAlmanacEditorState } from 'stores/EditorState'
import { useDocStore } from 'stores/DocStore'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { TextSelection } from 'prosemirror-state'
import { PromptInput } from 'components/PromptInput/PromptInput'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { autoUpdate, flip, hide, inline, offset, shift, useFloating } from '@floating-ui/react-dom'
import Styles from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/RevisionPromptInput.module.scss'
import { AI } from 'almanac-ui/icons/18'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { Popover } from 'components/Popover/Popover'
import { ReviseMenu } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/ReviseMenu'
import { RevisionType } from 'components/BlazeCommandLine/Types'
import { OffsetOptions } from '@floating-ui/core'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import cx from 'classnames'
import { ReactComponent as PostIcon } from 'assets/icons/post16.svg'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

export interface Props {
  isActive: boolean
  articleId: string
  layerId: string
  editorState: AlmanacEditorState
  onChange: (prompt: string) => any
  previousRevisionId?: string
  selection?: TextSelection
  customPrompt?: string
  menuOffsetOptions?: OffsetOptions
  submitIconSize?: 'xs' | 'sm' | 'md' | 'lg'
  inputSize?: 'sm' | 'md'
}

export const RevisionPromptInput = ({
  isActive,
  articleId,
  layerId,
  editorState,
  onChange,
  previousRevisionId,
  selection,
  customPrompt = '',
  menuOffsetOptions = { mainAxis: 10, crossAxis: -38 },
  submitIconSize = 'xs',
  inputSize = 'sm',
}: Props) => {
  const { mutate, isLoading } = useCreateRevision(
    { articleKey: articleId, layerId },
    {
      onSuccess: () => {
        const tr = editorState.view.state.tr
        tr.setSelection(TextSelection.create(editorState.view.state.doc, 0))
        editorState.view.dispatch(tr)
      },
    },
  )

  const { isMobile } = useResponsiveDesign()
  const { isOpen, close } = useOverlayTriggerState({ isOpen: isActive && customPrompt.trim().length < 4 })
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [inputContent, setInputContent] = useState('')
  const { x, y, reference, floating, middlewareData } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(menuOffsetOptions), inline(), flip(), shift(), hide()],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  useLayoutEffect(() => {
    reference(inputRef.current)
  }, [reference])

  const handleSubmit = () => {
    setInputContent('')
    const proseSelection = selection || editorState.selection
    const doc = editorState.view.state.doc
    const documentAsMarkdown = markdownSerializer.serialize(doc)
    const selectionContent = doc.cut(proseSelection.from, proseSelection.to)
    const selectionAsMarkdown = markdownSerializer.serialize(selectionContent)

    mutate(
      {
        prompt: { custom_prompt: customPrompt, revision_type: RevisionType.CUSTOM_PROMPT },
        selection: proseSelection.toJSON() as Selection,
        selectionAsMarkdown,
        documentAsMarkdown,
        previousRevisionId,
      },
      {
        onSettled: () => {
          onChange('')
        },
      },
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation()

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (customPrompt.trim().length > 2) handleSubmit()
    }
  }

  const isVisible = !middlewareData.hide?.referenceHidden && x !== null && y !== null

  return (
    <>
      <div className={cx(Styles.textField, Styles[inputSize], { [Styles.isFocused]: isActive })}>
        <div className={Styles.icon}>
          <AI className={Styles.icon} />
        </div>
        <PromptInput
          ref={inputRef}
          placeholder={isMobile ? 'Ask Blaze to refine...' : 'Ask Blaze to edit or generate...'}
          onKeyDown={handleKeyDown}
          isDisabled={isLoading}
          className={Styles.promptInput}
          classNameTextarea={Styles.textarea}
          prompt={customPrompt}
          onChange={(value: string) => {
            setInputContent(value)
            onChange(value)
          }}
          onSubmit={handleSubmit}
          withIcon={false}
        />
        {isOpen && !isMobile && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              style={{
                position: 'fixed',
                visibility: isVisible ? 'visible' : 'hidden',
                top: y || 0,
                left: x || 0,
              }}
              isOpen={isOpen}
              onClose={close}
              ref={floating}
              containFocus={false}
              restoreFocus
              noOverlay
              autoFocus={false}
              className={Styles.menu}
            >
              <ReviseMenu
                articleId={articleId}
                layerId={layerId}
                selection={selection}
                previousRevisionId={previousRevisionId}
              />
            </Popover>
          </FloatingPortal>
        )}
      </div>
      {isActive && (
        <IconButton
          aria-label="Ask Blaze"
          icon={PostIcon}
          variant={inputContent.trim().length > 2 ? 'primary' : 'tertiary'}
          isDisabled={inputContent.trim().length < 3 || isLoading}
          onPress={handleSubmit}
          size={submitIconSize}
        />
      )}
    </>
  )
}

export const RevisionPromptInputContainer = observer((props: Omit<Props, 'articleId' | 'layerId' | 'editorState'>) => {
  const editorState = useAlmanacEditorState()
  const docStore = useDocStore()

  if (!editorState || !docStore.layerId) {
    return null
  }

  return (
    <RevisionPromptInput
      articleId={docStore.articleId}
      layerId={docStore.layerId}
      editorState={editorState}
      {...props}
    />
  )
})
